import {
  getAccount as getEVMAccount,
  sendTransaction,
  switchChain,
  signTypedData,
  getChainId,
} from "wagmi/actions";
import { wagmiConfig } from "./wagmi";
import { Hex } from "viem";
import {
  CAIP2,
  PermitTypedData,
  Session,
  ViemTransaction,
} from "@paywithglide/glide-js";
import { getAccount as getSolanaAccount } from "./solana/getAccount";
import { signAndSendTransaction } from "./solana/signAndSendTransaction";
import { VersionedTransaction } from "@solana/web3.js";
import { glideLocalStorage } from "./glideLocalStorage";
import memStore from "./memStore";
import bs58 from "bs58";

function normalizeWalletAddress(address?: string | null) {
  if (!address) return undefined;
  const trimmed = address.trim();
  if (!trimmed) return undefined;
  const lowered = trimmed.toLowerCase();
  if (lowered === "null" || lowered === "undefined") return undefined;
  return trimmed;
}

export async function getConnectedWallet({
  mode: defaultMode,
  wagmiAccount,
  solanaAccount,
  useExternalWallet: uew,
  externalWalletEcosystem: ewe,
}: {
  mode?: string;
  wagmiAccount?: {
    chainId: number;
    address?: Hex;
    isConnected: boolean;
  };
  solanaAccount?: {
    address?: string;
    isConnected: boolean;
  };
  useExternalWallet?: boolean;
  externalWalletEcosystem?: "evm" | "solana";
} = {}) {
  const params = new URLSearchParams(window.location.search);
  const parentWalletAddress = normalizeWalletAddress(
    params.get("connectedWalletAddress") ||
      memStore.getItem("connectedWalletAddress"),
  ) as Hex | undefined;
  const parentChainId =
    params.get("currentChainId") || memStore.getItem("currentChainId");
  // Parse available chain IDs - could be numeric EVM or CAIP2 format (e.g., "solana:101")
  const parentAvailableChainIdsRaw = (
    params.get("availableChainIds") || memStore.getItem("availableChainIds")
  )?.split(",");

  // Detect if parent wallet is Solana based on chain ID format
  const isParentSolana =
    parentChainId?.startsWith("solana:") ||
    parentAvailableChainIdsRaw?.some((id) => id.startsWith("solana:"));

  const evmAddress = normalizeWalletAddress(
    wagmiAccount?.address || getEVMAccount(wagmiConfig()).address,
  ) as Hex | undefined;
  const isEVMConnected =
    wagmiAccount?.isConnected || getEVMAccount(wagmiConfig()).isConnected;
  const evmChainId = wagmiAccount?.chainId || getChainId(wagmiConfig());

  const mode = defaultMode || (await glideLocalStorage.getItem("glide.mode"));

  const useExternalWallet =
    mode &&
    mode !== "withdraw" &&
    (uew ||
      (await glideLocalStorage.getItem("glide.useExternalWallet")) === "true");

  const useParentWallet =
    (await glideLocalStorage.getItem("glide.useParentWallet")) === "true";

  const externalWalletEcosystem =
    ewe ||
    ((await glideLocalStorage.getItem("glide.externalWalletEcosystem")) ===
    "solana"
      ? "solana"
      : (await glideLocalStorage.getItem("glide.externalWalletEcosystem")) ===
          "evm"
        ? "evm"
        : undefined);

  const solanaAddress = normalizeWalletAddress(
    solanaAccount?.address || (await getSolanaAccount()).address,
  );
  const isSolanaConnected =
    solanaAccount?.isConnected || (await getSolanaAccount()).isConnected;

  const resolvedAddress =
    useExternalWallet && externalWalletEcosystem === "evm"
      ? evmAddress || (parentWalletAddress as Hex | undefined) // Use parent as fallback during init
      : useExternalWallet && externalWalletEcosystem === "solana"
        ? solanaAddress || parentWalletAddress
        : useParentWallet || mode === "withdraw"
          ? parentWalletAddress
          : undefined;

  // Determine if wallet is ready (not in initialization state)
  // Ready means the wallet provider (wagmi/solana) has finished initializing
  const isReady =
    useExternalWallet && externalWalletEcosystem === "evm"
      ? isEVMConnected // EVM wallet ready when wagmi reports connection
      : useExternalWallet && externalWalletEcosystem === "solana"
        ? isSolanaConnected // Solana wallet ready when solana reports connection
        : true; // Parent wallet is always "ready" since it comes from URL params

  // isConnected is derived from settings, but also requires a usable address.
  // This avoids null/empty wallet states being treated as connected.
  const isConnectedBySettings = useExternalWallet // If external wallet is enabled, we're "connected"
    ? true
    : (useParentWallet || mode === "withdraw") && Boolean(parentWalletAddress)
      ? true
      : false;
  const isConnected = isConnectedBySettings && Boolean(resolvedAddress);

  return {
    hasParentAppWallet: Boolean(parentWalletAddress),
    isConnected,
    isReady,
    canDisconnect: Boolean(
      isConnected && (useExternalWallet || useParentWallet),
    ),
    address: resolvedAddress,
    chainId:
      useExternalWallet && externalWalletEcosystem === "evm" && isEVMConnected
        ? evmChainId
        : useExternalWallet &&
            externalWalletEcosystem === "solana" &&
            isSolanaConnected
          ? 101
          : useParentWallet || mode === "withdraw"
            ? isParentSolana
              ? parseInt(parentChainId?.split(":")[1] || "101")
              : parentChainId
                ? Number(parentChainId)
                : undefined
            : undefined,
    availableChainIds: (useExternalWallet &&
    externalWalletEcosystem === "evm" &&
    isEVMConnected
      ? wagmiConfig().chains.map((c) => `eip155:${c.id}`)
      : useExternalWallet &&
          externalWalletEcosystem === "solana" &&
          isSolanaConnected
        ? ["solana:101", "solana:103"]
        : useParentWallet || mode === "withdraw"
          ? parentAvailableChainIdsRaw?.map((id) =>
              id.includes(":") ? id : `eip155:${id}`,
            )
          : undefined) as CAIP2[] | undefined,

    signAndSendSolanaTransaction: async (
      tx: VersionedTransaction,
      session: Session,
    ): Promise<string> => {
      // External Solana wallet path
      if (
        useExternalWallet &&
        externalWalletEcosystem === "solana" &&
        isSolanaConnected
      ) {
        return await signAndSendTransaction(tx);
      }

      // Parent Solana wallet path
      window.parent?.postMessage(
        {
          type: "solana_sendTransaction",
          transaction: Array.from(tx.serialize()),
          session,
        },
        "*",
      );

      return new Promise((resolve, reject) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.data?.type === "solana_didSendTransaction") {
            window.removeEventListener("message", handleMessage);
            if (event.data?.success) {
              // Handle signature - could be base58 string, Uint8Array, or regular array
              const rawSignature = event.data.signature;
              let signature: string;
              if (typeof rawSignature === "string") {
                // Already a base58 string
                signature = rawSignature;
              } else if (Array.isArray(rawSignature)) {
                // Regular array from postMessage - convert to Uint8Array then base58
                signature = bs58.encode(new Uint8Array(rawSignature));
              } else if (rawSignature instanceof Uint8Array) {
                // Uint8Array
                signature = bs58.encode(rawSignature);
              } else if (
                typeof rawSignature === "object" &&
                rawSignature !== null
              ) {
                // Object with numeric keys (like {0: 1, 1: 2, ...}) - convert to array
                const arr = Object.values(rawSignature) as number[];
                signature = bs58.encode(new Uint8Array(arr));
              } else {
                throw new Error(
                  `Unexpected signature type: ${typeof rawSignature}`,
                );
              }
              resolve(signature);
            } else {
              reject(event.data?.error);
            }
          }
        };

        window.addEventListener("message", handleMessage);
      });
    },

    switchChainAsync: async ({
      chainId,
    }: {
      chainId: number;
    }): Promise<unknown> => {
      if (
        useExternalWallet &&
        externalWalletEcosystem === "evm" &&
        isEVMConnected
      ) {
        // @ts-expect-error: chain id is not constrained
        return switchChain(wagmiConfig(), { chainId });
      }

      window.parent?.postMessage(
        {
          type: "switchChain",
          chainId,
        },
        "*",
      );

      return new Promise((success, fail) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.data?.type === "didSwitchChain") {
            window.removeEventListener("message", handleMessage);
            if (event.data?.success) {
              success(event.data?.chainId);
            } else {
              fail(event.data?.error);
            }
          }
        };

        window.addEventListener("message", handleMessage);
      });
    },

    sendTransactionAsync: async (
      tx: ViemTransaction<number>,
      session: Session,
    ): Promise<Hex | { deferred: true }> => {
      if (
        useExternalWallet &&
        externalWalletEcosystem === "evm" &&
        isEVMConnected
      ) {
        // @ts-expect-error: chain id is not constrained
        return sendTransaction(wagmiConfig(), tx);
      }

      window.parent?.postMessage(
        {
          type: "sendTransaction",
          tx,
          session,
        },
        "*",
      );

      return new Promise((success, fail) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.data?.type === "didSendTransaction") {
            window.removeEventListener("message", handleMessage);
            if (event.data?.deferred) {
              success({ deferred: true });
            } else if (event.data?.success) {
              success(event.data?.txHash);
            } else {
              fail(event.data?.error);
            }
            return;
          }
        };

        window.addEventListener("message", handleMessage);
      });
    },

    signTypedDataAsync: async (
      data: PermitTypedData<bigint>,
      session: Session,
    ): Promise<Hex> => {
      if (
        useExternalWallet &&
        externalWalletEcosystem === "evm" &&
        isEVMConnected
      ) {
        // @ts-expect-error: permit data types are not compatible
        return signTypedData(wagmiConfig(), data);
      }

      window.parent?.postMessage(
        {
          type: "signTypedData",
          data,
          session,
        },
        "*",
      );

      return new Promise((success, fail) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.data?.type === "didSignTypedData") {
            window.removeEventListener("message", handleMessage);
            if (event.data?.success) {
              success(event.data?.signature);
            } else {
              fail(event.data?.error);
            }
            return;
          }
        };

        window.addEventListener("message", handleMessage);
      });
    },
  };
}
